import React, {useMemo, useState, useEffect, useRef} from 'react';
import axios from 'axios';
import {ColumnDef} from '@tanstack/react-table';
import {DataGrid, KeenIcon} from '@/components';
import {Loading} from '@/layouts/buddy/loading';
import {enqueueSnackbar} from 'notistack';
import {
    CPSPromptData,
    TagEnum,
    tagColorMap,
    TAG_ENUM_VALUES,
} from '@/pages/buddy/types/CPSPromptData';
import clsx from 'clsx';

// helper to format tag strings
const formatTag = (tag: string): string => {
    return tag
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

export const CPSPrompts: React.FC<{ cpsPrompts: CPSPromptData[]; loading: boolean; }> = ({cpsPrompts, loading}) => {
    const [internalPrompts, setInternalPrompts] = useState<CPSPromptData[] | null>(null);
    const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('cpsPrompts-filter') || '');
    const [selectedTags, setSelectedTags] = useState<TagEnum[]>([]);
    const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<CPSPromptData | null>(null);
    const [newPromptMode, setNewPromptMode] = useState(false);
    const [formData, setFormData] = useState<Omit<CPSPromptData, 'p_id'>>({
        title: '',
        tags: [],
        summary: '',
        content: '',
        is_active: true,
        is_deleted: false,
    });

    const tagRef = useRef<HTMLDivElement>(null);

    const fetchPrompts = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${import.meta.env.VITE_APP_API_URL}/admin/cps-prompts`, {
                headers: {Authorization: `Bearer ${token}`},
            });
            setInternalPrompts(response.data);
        } catch (err: any) {
            enqueueSnackbar(`Failed to fetch prompts: ${err.message || err}`, {variant: 'error'});
        }
    };

    useEffect(() => {
        fetchPrompts();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (tagRef.current && !tagRef.current.contains(e.target as Node)) {
                setTagDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        localStorage.setItem('cpsPrompts-filter', searchTerm);
    }, [searchTerm]);

    const handleSave = async () => {
        try {
            const url = editingPrompt
                ? `${import.meta.env.VITE_APP_API_URL}/admin/cps-prompt/${editingPrompt.p_id}`
                : `${import.meta.env.VITE_APP_API_URL}/admin/cps-prompt`;
            const method = editingPrompt ? 'put' : 'post';
            const token = localStorage.getItem('authToken');

            await axios({
                url,
                method,
                data: formData,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            enqueueSnackbar(editingPrompt ? 'Prompt updated' : 'Prompt created', {variant: 'success'});

            await fetchPrompts();
            setEditingPrompt(null);
            setNewPromptMode(false);
        } catch (err: any) {
            enqueueSnackbar(`Failed to save prompt: ${err.message || err}`, {variant: 'error'});
        }
    };

    const filteredData = useMemo(() => {
        const data = internalPrompts ?? cpsPrompts;
        return data.filter((p) => {
            const byText = `${p.title} ${p.summary}`.toLowerCase().includes(searchTerm.toLowerCase());
            const byTags = selectedTags.length === 0 || selectedTags.every((t) => p.tags.includes(t));
            return byText && byTags;
        });
    }, [internalPrompts, cpsPrompts, searchTerm, selectedTags]);

    const toggleTag = (tag: TagEnum) =>
        setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

    const columns = useMemo<ColumnDef<CPSPromptData>[]>(
        () => [
            {
                accessorKey: 'title', enableSorting: true,
                header: 'Title',
                cell: ({row}) => (
                    <div className="leading-none font-medium text-sm text-gray-900">
                        {row.original.title}
                    </div>
                ),
            },
            {
                accessorKey: 'summary', enableSorting: true,
                header: 'Summary',
                cell: ({row}) => (
                    <div className="text-sm text-gray-700 font-normal">
                        {row.original.summary}
                    </div>
                ),
            },
            {
                accessorKey: 'tags', enableSorting: true,
                header: 'Tags',
                cell: ({row}) => (
                    <div className="flex flex-wrap gap-2">
                        {row.original.tags.map((tag) => (
                            <span key={tag} className={clsx('badge badge-sm', tagColorMap[tag])}>
                                {formatTag(tag)}
                            </span>
                        ))}
                    </div>
                ),
            },
            {
                accessorKey: 'content', enableSorting: true,
                header: 'Content',
                cell: ({row}) => (
                    <div className="line-clamp-2 text-gray-700 font-normal">
                        {row.original.content}
                    </div>
                ),
            },
            {
                accessorKey: 'is_active', enableSorting: true,
                header: 'Active',
                cell: ({row}) => (
                    <div className="text-gray-700 font-normal">
                        {row.original.is_active ? 'Yes' : 'No'}
                    </div>
                ),
            },
            {
                id: 'edit',
                header: () => 'Edit',
                enableSorting: true,
                cell: ({row}) => (
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => {
                            setEditingPrompt(row.original);
                            setFormData({
                                title: row.original.title,
                                tags: row.original.tags,
                                summary: row.original.summary,
                                content: row.original.content,
                                is_active: row.original.is_active,
                                is_deleted: row.original.is_deleted,
                            });
                        }}
                    >
                        <KeenIcon icon="notepad-edit"/>
                    </button>
                ),
                meta: {
                    className: 'w-[60px]',
                },
            },
        ],
        []
    );

    if (loading) return <Loading loading={loading}/>;

    return (
        <>
            <div className="card card-grid">
                <div className="card-header flex justify-between items-center">
                    <h3 className="card-title">CPS Prompts</h3>
                    <div className="flex gap-3 items-center">
                        <div className="relative">
                            <KeenIcon icon="magnifier"
                                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"/>
                            <input
                                className="input input-sm pl-10"
                                placeholder="Search prompts"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="relative w-48" ref={tagRef}>
                            <div
                                className="input input-sm w-full flex justify-between cursor-pointer"
                                onClick={() => setTagDropdownOpen((o) => !o)}
                            >
                                <span className="truncate">
                                    {selectedTags.length
                                        ? `Tags: ${selectedTags.map(formatTag).join(', ')}`
                                        : 'Filter by tags'}
                                </span>
                                <KeenIcon icon="chevron-down" className="text-gray-500"/>
                            </div>
                            {tagDropdownOpen && (
                                <div
                                    className="absolute z-10 mt-1 bg-white border rounded shadow max-h-60 overflow-auto w-full p-2">
                                    {TAG_ENUM_VALUES.map((tag) => (
                                        <label key={tag}
                                               className="flex items-center gap-2 p-1 hover:bg-gray-50 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedTags.includes(tag)}
                                                onChange={() => toggleTag(tag)}
                                                className="checkbox checkbox-sm"
                                            />
                                            <span className={clsx('badge badge-sm', tagColorMap[tag])}>
                                                {formatTag(tag)}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button className="btn btn-sm btn-primary" onClick={() => {
                            setNewPromptMode(true);
                            setEditingPrompt(null);
                            setFormData({
                                title: '',
                                tags: [],
                                summary: '',
                                content: '',
                                is_active: true,
                                is_deleted: false
                            });
                        }}>
                            <KeenIcon icon="plus"/> New CPS Prompt
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    <DataGrid columns={columns} data={filteredData} pagination={{size: 10}}/>
                </div>
            </div>

            {(editingPrompt || newPromptMode) && (
                <div className="card p-5 mt-10">
                    <h4 className="text-lg font-medium mb-3">
                        {editingPrompt ? 'Edit CPS Prompt' : 'New CPS Prompt'}
                    </h4>
                    <div className="grid gap-6">
                        <input type="text" className="input input-md input-bordered" placeholder="Title"
                               value={formData.title}
                               onChange={(e) => setFormData({...formData, title: e.target.value})}/>
                        <textarea className="textarea textarea-bordered w-full" placeholder="Summary" rows={3}
                                  value={formData.summary}
                                  onChange={(e) => setFormData({...formData, summary: e.target.value})}/>
                        <textarea className="textarea textarea-bordered w-full" placeholder="Prompt Content" rows={5}
                                  value={formData.content}
                                  onChange={(e) => setFormData({...formData, content: e.target.value})}/>
                        <div className="flex flex-wrap gap-2">
                            {TAG_ENUM_VALUES.map((tag) => (
                                <label key={tag} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm"
                                        checked={formData.tags.includes(tag)}
                                        onChange={() => {
                                            const next = formData.tags.includes(tag)
                                                ? formData.tags.filter((t) => t !== tag)
                                                : [...formData.tags, tag];
                                            setFormData({...formData, tags: next});
                                        }}
                                    />
                                    <span className={clsx('badge badge-sm', tagColorMap[tag])}>
                                        {formatTag(tag)}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="flex gap-6">
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={formData.is_active}
                                       onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                       className="checkbox checkbox-sm"/>
                                <span>Active</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={formData.is_deleted}
                                       onChange={(e) => setFormData({...formData, is_deleted: e.target.checked})}
                                       className="checkbox checkbox-sm"/>
                                <span>Deleted</span>
                            </label>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button className="btn btn-light btn-sm" onClick={() => {
                                setEditingPrompt(null);
                                setNewPromptMode(false);
                            }}>
                                Cancel
                            </button>
                            <button className="btn btn-primary btn-sm" onClick={handleSave}>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
